import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { GasPump, Percent, Calendar, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Settings() {
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;

  const save = async () => {
    try {
      const { data } = await api.put("/settings", {
        diesel_price: s.diesel_price, default_vat_pct: s.default_vat_pct,
        default_margin_pct: s.default_margin_pct, working_days_per_month: s.working_days_per_month,
        turnaround_buffer_min: s.turnaround_buffer_min,
        standing_rate_per_hour: s.standing_rate_per_hour, overtime_rate_per_hour: s.overtime_rate_per_hour,
        assistant_rate: s.assistant_rate, tail_lift_default_rate: s.tail_lift_default_rate,
        additional_charge_presets: (s.additional_charge_presets || []).filter((p) => p.label?.trim()).map((p) => ({ label: p.label, amount: String(p.amount || "0") })),
      });
      setS(data); toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fields = [
    { k: "diesel_price", label: "Diesel price (R / litre)", icon: GasPump, hint: "Master fuel price. Saving syncs it into every vehicle profile and all fuel calcs." },
    { k: "default_margin_pct", label: "Default target margin (%)", icon: Percent, hint: "Selling price = cost base ÷ (1 − margin)." },
    { k: "default_vat_pct", label: "Default VAT (%)", icon: Percent, hint: "South Africa standard rate." },
    { k: "working_days_per_month", label: "Working days / month", icon: Calendar, hint: "Daily fixed unit = monthly fixed ÷ this." },
    { k: "turnaround_buffer_min", label: "Turnaround buffer (min)", icon: Clock, hint: "Warn when trips fall within this gap on a vehicle." },
    { k: "standing_rate_per_hour", label: "Driver standing rate (R / hour)", icon: Clock, hint: "Shown in quote T&Cs — charged for additional driver standing time." },
    { k: "overtime_rate_per_hour", label: "Driver overtime rate (R / hour)", icon: Clock, hint: "Shown in quote T&Cs — charged for overtime not included in the quote." },
    { k: "assistant_rate", label: "Assistant rate (R / each)", icon: Clock, hint: "Charged per assistant added to a quote's additional charges." },
    { k: "tail_lift_default_rate", label: "Tail-lift default rate (R)", icon: Clock, hint: "Pre-fills the tail-lift charge on new quotes. Can be overwritten per quote." },
  ];

  const presets = s.additional_charge_presets || [];
  const setPresets = (next) => setS({ ...s, additional_charge_presets: next });

  return (
    <div>
      <PageHeader title="Settings" subtitle="Global costing and scheduling defaults." />
      <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-6 space-y-5">
        {fields.map((f) => (
          <div key={f.k} className="flex items-start justify-between gap-6 py-1">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0"><f.icon size={20} className="text-slate-700" weight="bold" /></div>
              <div className="min-w-0">
                <label className="block text-sm font-medium text-slate-900">{f.label}</label>
                <p className="text-xs text-slate-400 mt-0.5 max-w-md">{f.hint}</p>
              </div>
            </div>
            <div className="w-40 shrink-0">
              <input data-testid={`setting-${f.k}`} className="cd-input data-num" value={s[f.k] || ""} onChange={(e) => setS({ ...s, [f.k]: e.target.value })} />
            </div>
          </div>
        ))}
        <div className="pt-2 flex items-center justify-between">
          <span className="text-xs text-slate-400 font-mono">{s.timezone} · {s.currency}</span>
          <Button data-testid="settings-save" onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">Save settings</Button>
        </div>
      </div>

      {/* Additional charge presets */}
      <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-6 mt-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-sm font-medium text-slate-900">Additional-charge presets</h3>
            <p className="text-xs text-slate-400 mt-0.5 max-w-md">One-tap presets (e.g. Crane hire, Waiting fee) shown on the quote calculator. Tapping one fills the additional charge; it can still be overwritten.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setPresets([...presets, { label: "", amount: "0" }])} data-testid="add-preset-btn">Add preset</Button>
        </div>
        {presets.length === 0 ? (
          <div className="text-xs text-slate-400 bg-slate-50 rounded-md px-3 py-2">No presets yet — add common charges to speed up quoting.</div>
        ) : (
          <div className="space-y-2">
            {presets.map((p, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`preset-row-${i}`}>
                <input className="cd-input flex-1" placeholder="Label (e.g. Crane hire)" value={p.label} onChange={(e) => setPresets(presets.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} data-testid={`preset-label-${i}`} />
                <input className="cd-input data-num w-32" placeholder="0.00" value={p.amount} onChange={(e) => setPresets(presets.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} data-testid={`preset-amount-${i}`} />
                <button onClick={() => setPresets(presets.filter((_, idx) => idx !== i))} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`preset-remove-${i}`}>✕</button>
              </div>
            ))}
          </div>
        )}
        <div className="pt-4 flex justify-end">
          <Button data-testid="presets-save" onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">Save presets</Button>
        </div>
      </div>

      <TollGates />
    </div>
  );
}

function TollGates() {
  const [gates, setGates] = useState([]);
  const load = () => api.get("/toll-gates").then((r) => setGates(r.data));
  useEffect(() => { load(); }, []);
  const [draft, setDraft] = useState(null);
  const blank = { name: "", rate_class_1: "0", rate_class_2: "0", rate_class_3: "0", rate_class_4: "0", active: true };

  const saveGate = async (g) => {
    try {
      const payload = { name: g.name, rate_class_1: String(g.rate_class_1 || "0"), rate_class_2: String(g.rate_class_2 || "0"), rate_class_3: String(g.rate_class_3 || "0"), rate_class_4: String(g.rate_class_4 || "0"), active: true };
      if (g.id) await api.patch(`/toll-gates/${g.id}`, payload);
      else await api.post("/toll-gates", payload);
      toast.success("Toll gate saved"); setDraft(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/toll-gates/${id}`); toast.success("Toll gate removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="max-w-3xl bg-white border border-slate-200 rounded-md p-6 mt-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-sm font-medium text-slate-900">Toll-gate catalogue</h3>
          <p className="text-xs text-slate-400 mt-0.5 max-w-lg">Define each toll gate once with a rate per SANRAL class. On a quote, staff tick the gates for the selected vehicle's class and the total flows into tolls.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setDraft(blank)} data-testid="add-gate-btn">Add gate</Button>
      </div>
      <div className="grid grid-cols-[1fr_repeat(4,72px)_92px] gap-2 items-center text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
        <span>Gate</span><span className="text-right">Class 1</span><span className="text-right">Class 2</span><span className="text-right">Class 3</span><span className="text-right">Class 4</span><span></span>
      </div>
      <div className="space-y-1">
        {gates.map((g) => (
          <GateRow key={g.id} gate={g} onSave={saveGate} onDelete={() => del(g.id)} />
        ))}
        {draft && <GateRow gate={draft} isNew onSave={saveGate} onCancel={() => setDraft(null)} />}
        {gates.length === 0 && !draft && <div className="text-xs text-slate-400 bg-slate-50 rounded-md px-3 py-2">No toll gates yet — add your common gates to enable the quote picker.</div>}
      </div>
    </div>
  );
}

function GateRow({ gate, isNew, onSave, onDelete, onCancel }) {
  const [g, setG] = useState(gate);
  useEffect(() => { setG(gate); }, [gate]);
  const up = (k, v) => setG({ ...g, [k]: v });
  return (
    <div className="grid grid-cols-[1fr_repeat(4,72px)_92px] gap-2 items-center" data-testid={isNew ? "gate-row-new" : `gate-row-${gate.id}`}>
      <input className="cd-input" placeholder="e.g. N3 De Hoek Plaza" value={g.name} onChange={(e) => up("name", e.target.value)} data-testid="gate-name" />
      {["rate_class_1", "rate_class_2", "rate_class_3", "rate_class_4"].map((k) => (
        <input key={k} className="cd-input data-num text-right" value={g[k]} onChange={(e) => up(k, e.target.value)} data-testid={`gate-${k}`} />
      ))}
      <div className="flex items-center gap-1">
        <Button size="sm" onClick={() => onSave(g)} className="bg-teal-600 hover:bg-teal-700 text-white h-8 px-2 text-xs" data-testid="gate-save">Save</Button>
        {isNew ? <button onClick={onCancel} className="p-1.5 text-slate-400 hover:text-slate-700 text-xs">✕</button>
               : <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-600" data-testid="gate-delete">✕</button>}
      </div>
    </div>
  );
}
